import { useEffect, useCallback } from 'react';
import BottomPanel from './BottomPanel';
import { FEATHER_MAP } from './featherIcons';
import { renderDestIcon } from './blockDestIcon';
import useBlockEdit from '../hooks/useBlockEdit';

/**
 * Universal block editor — the bottom panel (was BlockInputModal on desktop +
 * BlockComposer on iOS). All platforms share this one component + `useBlockEdit`
 * so magic-word auto-save, IME handling, draft recovery, and timeline actions
 * stay identical. The keyboard-dock positioning lives in `BottomPanel`.
 *
 * Layout: the textarea on top, then a right-aligned row of action icons
 * (timeline / dismiss / confirm / delete), then the "Move to" chips in the
 * footer — with vertical breathing room between the input and the move-to row.
 *
 * Props: same surface as the old BlockInputModal / BlockComposer.
 */
const ClockIcon = FEATHER_MAP['clock'];

export default function BlockEditorPanel({
  show,
  initialContent = '',
  placeholder = 'Write something...',
  onConfirm,
  onClose,
  onDelete,
  destinations,
  onMoveToDestination,
  autoSaveWord = 'over',
  autoSaveDelay = 3000,
  showTimelineActions = false,
  onMarkCopy,
  onMarkMove,
  filePath = '',
  blockIndex = null,
  originalContent = null,
}) {
  const {
    content,
    contentRef,
    waitingToConfirm,
    timelineMenuOpen,
    setTimelineMenuOpen,
    isEdit,
    textareaRef,
    tlMenuRef,
    handleClose,
    handleConfirm,
    cancelWaiting,
    handleChange,
    handleKeyDown,
    handleCompositionStart,
    handleCompositionEnd,
  } = useBlockEdit({
    show,
    initialContent,
    onConfirm,
    onClose,
    autoSaveWord,
    autoSaveDelay,
    filePath,
    blockIndex,
    originalContent,
  });

  // ── Textarea auto-grow 3→8 lines (16px font → no iOS focus zoom) ──
  const autoGrow = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 212)}px`;
  }, [textareaRef]);

  useEffect(() => {
    if (show) autoGrow();
  }, [content, show, autoGrow]);

  // Timeline clock → dropdown of the two mark actions (sits in the bottom
  // action row, right-aligned alongside dismiss / confirm / delete).
  const timelineButton = showTimelineActions ? (
    <div className="tl" ref={tlMenuRef}>
      <button
        className="bp-btn"
        type="button"
        title="Timeline"
        aria-haspopup="menu"
        aria-expanded={timelineMenuOpen}
        onClick={() => setTimelineMenuOpen((v) => !v)}
      >
        <ClockIcon size={18} strokeWidth={2} />
      </button>
      {timelineMenuOpen && (
        <div className="tl-menu" role="menu">
          <button
            className="tl-item"
            role="menuitem"
            type="button"
            onClick={() => {
              setTimelineMenuOpen(false);
              const blockText = contentRef.current.trim();
              if (onMarkCopy) onMarkCopy(blockText);
            }}
          >
            Time record
          </button>
          <button
            className="tl-item"
            role="menuitem"
            type="button"
            onClick={() => {
              setTimelineMenuOpen(false);
              const blockText = contentRef.current.trim();
              if (onMarkMove) onMarkMove(blockText);
            }}
          >
            Time record and move to timeline
          </button>
        </div>
      )}
    </div>
  ) : null;

  // Countdown strip — shown between the input and the action row while the
  // magic-word auto-save awaits confirmation.
  const waitingStrip = waitingToConfirm ? (
    <div className="bp-wait">
      <span className="wait-label">Close &amp; Save</span>
      <div className="progress" role="progressbar" aria-label="Auto-save countdown">
        <div
          className="progress-fill"
          style={{ animationDuration: `${autoSaveDelay}ms` }}
        />
      </div>
    </div>
  ) : null;

  return (
    <BottomPanel
      show={show}
      onClose={handleClose}
      showClose={false}
      draggable
      footer={
        <div className="move-row">
          <span className="move-label">Move to</span>
          <div className="move-chips">
            {(destinations || []).map((dest) => (
              <button
                key={dest.type === 'node' ? dest.path : dest.type}
                className="move-chip"
                title={`Move to ${dest.label}`}
                onClick={() => {
                  const blockText = contentRef.current.trim();
                  if (onMoveToDestination) onMoveToDestination(dest, blockText);
                }}
                type="button"
              >
                {renderDestIcon(dest)}
              </button>
            ))}
          </div>
        </div>
      }
    >
      <textarea
        ref={textareaRef}
        className="bp-textarea"
        value={content}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={placeholder}
      />

      {waitingStrip}

      {/* Action icons — one line, right-aligned: timeline / confirm / delete
          (dismiss is the scrim tap, so no dedicated ✕ here). */}
      <div className="bp-actions">
        {timelineButton}

        {waitingToConfirm ? (
          <button className="bp-btn cancel" onClick={cancelWaiting} type="button">
            Cancel
          </button>
        ) : (
          <button
            className="bp-btn save"
            onClick={handleConfirm}
            disabled={!content.trim()}
            title="Save"
            aria-label="Save"
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </button>
        )}

        {isEdit && onDelete && (
          <button
            className="bp-btn delete"
            onClick={() => onDelete()}
            title="Delete"
            aria-label="Delete"
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}
      </div>
    </BottomPanel>
  );
}
